import robustFetch from "@/utils/robustFetch";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import SEOMeta from "@/components/SEOMeta";
import AggregateRating from "@/components/AggregateRating";
import QuickComparisonCard from "@/components/home/QuickComparisonCard";
import SecurityTrustBar from "@/components/home/SecurityTrustBar";
import { ConversionDataService } from "@/services/conversionDataService";
import { buildFormatPairConverterSlug } from "@shared/slug";
import { formatFileSize } from "@/services/planService";
import { lazy, Suspense, useEffect, useState, useRef, ReactNode } from "react";
import { logger } from "@/utils/logger";
import { useErrorHandler } from "@/components/ErrorBoundary";
import { cn } from "@/lib/utils";
import {
  FileImage,
  FileVideo,
  FileText,
  FileAudio,
  ArrowRight,
  Zap,
  Shield,
  CheckCircle,
  Star,
  Boxes,
  Archive,
  Presentation,
  Database,
  Globe,
  StickyNote,
  Layers3,
  MousePointer,
  Crown,
  TrendingUp,
  Search,
  Loader2,
  X,
  ScanText,
} from "lucide-react";

interface DynamicStats {
  formatsCount: number;
  categoriesCount: number;
  conversionPairsCount: number;
  totalConversions: number;
  successRate: number;
}

interface PopularConversion {
  from: string;
  to: string;
  icon: string;
  color: string;
  category: string;
  seoTitle: string;
  description: string;
  usageCount?: number;
  ratingValue?: number;
  votingCount?: number;
}

// Render sections immediately to avoid CLS from fixed-height placeholders
function LazySection({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

const DevQuickStart = lazy(() => import("@/components/DevQuickStart"));
// The homepage hero embeds the same conversion engine used on /convert
// (uploader, format selectors, batch/plan limits, results) via ConvertPage's
// `embedded` prop, rather than duplicating that logic. Lazy-loaded per
// .builderrules: the uploader is a heavy, interactive feature and must not
// bloat the homepage's initial JS bundle.
const ConvertPage = lazy(() => import("@/pages/ConvertPage"));

// Stable object identity so embedding ConvertPage on the homepage doesn't
// re-trigger its internal effects (which depend on `routeAnalysis`) every
// time Index re-renders (stats polling, search debounce, etc.).
const HOME_ROUTE_ANALYSIS = {
  type: "basic" as const,
  params: {},
  isValid: true,
};

export default function Index() {
  // Render immediately with default values - no blocking API calls
  const [dynamicStats, setDynamicStats] = useState<DynamicStats>({
    formatsCount: 432,
    categoriesCount: 18,
    conversionPairsCount: 33554,
    totalConversions: 150000000,
    successRate: 99.9,
  });
  const [popularConversions, setPopularConversions] = useState<
    PopularConversion[]
  >([]);
  const [isLoading, setIsLoading] = useState(true); // Start as true to show loading state

  console.log(
    "🔍 Index render - popularConversions:",
    popularConversions.length,
    "isLoading:",
    isLoading,
  );
  const [totalStats, setTotalStats] = useState<any>(null);
  const [reviewData, setReviewData] = useState<{
    averageRating: number;
    totalReviews: number;
  }>({
    averageRating: 4.8,
    totalReviews: 638,
  });
  const [hasError, setHasError] = useState(false);
  const reportError = useErrorHandler();
  const [heroSearchTerm, setHeroSearchTerm] = useState("");
  const [heroSearchResults, setHeroSearchResults] = useState<any[]>([]);
  const [heroSearchLoading, setHeroSearchLoading] = useState(false);
  const [showHeroResults, setShowHeroResults] = useState(false);
  const heroSearchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounced hero search
  useEffect(() => {
    if (heroSearchTerm.length < 2) {
      setHeroSearchResults([]);
      setHeroSearchLoading(false);
      setShowHeroResults(false);
      return;
    }

    setHeroSearchLoading(true);
    setShowHeroResults(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await robustFetch(
          `/api/search/global?q=${encodeURIComponent(heroSearchTerm)}`,
          {
            timeout: 5000,
            retries: 1,
          },
        );

        if (response.ok) {
          const data = await response.json();
          setHeroSearchResults(data.results || []);
        } else {
          setHeroSearchResults([]);
        }
      } catch (error) {
        console.error("Hero search error:", error);
        setHeroSearchResults([]);
      } finally {
        setHeroSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [heroSearchTerm]);

  // Note: Removed auto-hide on click outside - panel stays open until user manually closes it

  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent navigation on Enter - keep results visible for user selection
  };

  const handleHeroResultClick = () => {
    setShowHeroResults(false);
    setHeroSearchTerm("");
    setHeroSearchResults([]);
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case "format":
        return "📄";
      case "conversion":
        return "🔄";
      case "category":
        return "📁";
      case "view-all":
        return "📋";
      default:
        return "📌";
    }
  };

  const getResultBadgeColor = (type: string) => {
    switch (type) {
      case "format":
        return "bg-blue-100 text-blue-700";
      case "conversion":
        return "bg-green-100 text-green-700";
      case "category":
        return "bg-purple-100 text-purple-700";
      case "view-all":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Load verified stats AFTER initial render (non-blocking)
  useEffect(() => {
    const loadInitialStats = async () => {
      try {
        setHasError(false);
        const stats = await ConversionDataService.getVerifiedStats();
        setDynamicStats(stats);

        logger.info("Initial stats loaded", {
          context: "Index.loadInitialStats",
          metadata: { statsCount: stats ? Object.keys(stats).length : 0 },
        });
      } catch (error) {
        setHasError(true);
        // Silently use fallback values - not a critical error
        logger.debug("Stats API unavailable, using fallback values", {
          context: "Index.loadInitialStats",
          metadata: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
    };

    // Defer stats loading using requestIdleCallback for non-blocking behavior
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(() => loadInitialStats());
      return () => cancelIdleCallback(id);
    } else {
      const timeoutId = setTimeout(loadInitialStats, 100);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  // Load aggregate review data across ALL conversion types
  useEffect(() => {
    const loadReviewData = async () => {
      try {
        const response = await robustFetch("/api/reviews/stats/total", {
          timeout: 5000,
          retries: 1,
          fallbackData: {
            success: true,
            data: { averageRating: 4.8, totalReviews: 638 },
          },
        });

        if (!response.ok) {
          console.warn(`⚠️ Review API returned ${response.status}`);
          return;
        }

        const text = await response.text();

        if (!text || text.trim().length === 0) {
          console.warn("⚠️ Review API returned empty response");
          return;
        }

        let data;
        try {
          data = JSON.parse(text);
        } catch (jsonError) {
          console.error(
            "❌ Failed to parse review JSON:",
            text.substring(0, 100),
          );
          return;
        }

        if (data.success && data.data) {
          setReviewData({
            averageRating: data.data.averageRating || 4.8,
            totalReviews: data.data.totalReviews || 638,
          });
        }
      } catch (error) {
        // Silently use default values - error already logged by robustFetch
      }
    };

    const schedule = () => loadReviewData();
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(schedule, { timeout: 2000 });
      return () => cancelIdleCallback(id);
    }
    const timeoutId = setTimeout(schedule, 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  // Load popular conversions on mount
  useEffect(() => {
    console.log("🚀 Starting to load popular conversions...");

    const loadPopularConversions = async () => {
      try {
        setIsLoading(true);
        console.log(
          "📡 Calling ConversionDataService.getPopularConversions()...",
        );

        const conversions = await ConversionDataService.getPopularConversions();

        console.log("✅ Received conversions:", {
          count: conversions.length,
          sample: conversions.slice(0, 2),
          allData: conversions,
        });

        if (!conversions || conversions.length === 0) {
          console.warn("⚠️ No conversions returned from service!");
        }

        setPopularConversions(conversions);
        setIsLoading(false);

        logger.info("Popular conversions loaded", {
          context: "Index.loadPopularConversions",
          metadata: { conversionsCount: conversions.length },
        });
      } catch (error) {
        console.error("❌ Error loading conversions:", error);
        setIsLoading(false);
        setPopularConversions([]); // Ensure empty array on error

        logger.warn("Failed to load popular conversions", {
          context: "Index.loadPopularConversions",
          metadata: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
    };

    const schedule = () => loadPopularConversions();
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(schedule, { timeout: 2000 });
      return () => cancelIdleCallback(id);
    }
    const timeoutId = setTimeout(schedule, 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  // Defer total stats fetching until page is idle (not critical for above-the-fold)
  useEffect(() => {
    let mounted = true;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const fetchStats = async () => {
      if (!mounted) return;

      try {
        const { robustFetchJSON } = await import("@/utils/robustFetch");
        const json = await robustFetchJSON("/api/stats/total-conversions", {
          retries: 2,
          timeout: 8000,
          fallbackData: {
            data: {
              overall: {
                totalFilesAll: 0,
                totalFilesCompleted: 0,
                totalSizeAll: 0,
                totalSizeCompleted: 0,
              },
            },
          },
        });

        if (!mounted) return;
        if (json?.data) {
          setTotalStats(json.data);
        }
      } catch (e) {
        // Silently handle errors - fallback data will be used
        if (mounted) {
          setTotalStats({
            overall: {
              totalFilesAll: 0,
              totalFilesCompleted: 0,
              totalSizeAll: 0,
              totalSizeCompleted: 0,
            },
          });
        }
      }
    };

    // Defer stats polling to after page interactive
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(() => {
        fetchStats();
        intervalId = setInterval(fetchStats, 5 * 60 * 1000);
      });
      return () => {
        mounted = false;
        cancelIdleCallback(id);
        if (intervalId) clearInterval(intervalId);
      };
    } else {
      // Fallback to setTimeout (5 seconds delay)
      const timeoutId = setTimeout(() => {
        fetchStats();
        intervalId = setInterval(fetchStats, 5 * 60 * 1000);
      }, 5000);
      return () => {
        mounted = false;
        clearTimeout(timeoutId);
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, []);

  // Icon component resolver
  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: any } = {
      FileImage,
      FileVideo,
      FileText,
      FileAudio,
      Boxes,
      Archive,
    };
    return iconMap[iconName] || FileText;
  };
  // Structured data as per Google Search policies 2024-2026
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Docpose.cloud",
      url: "https://docpose.cloud/",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Docpose.cloud",
      url: "https://docpose.cloud/",
      logo: "https://docpose.cloud/app-logo.png",
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Docpose.cloud",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      url: "https://docpose.cloud/",
      description:
        "Online file converter with OCR, REST API supporting 432+ formats including documents, images, audio, video, CAD, and 3D files with 33K+ conversion pairs. Extract text from scanned PDFs and images in 77 languages.",
      offers: {
        "@type": "Offer",
        url: "https://docpose.cloud/pricing",
        priceCurrency: "USD",
      },
    },
  ];

  const supportedCategories = [
    {
      category: "Advanced Image Processing",
      icon: FileImage,
      count: 110,
      link: "/image-converter",
      formats: [
        "JPEG",
        "PNG",
        "WebP",
        "AVIF",
        "TIFF",
        "SVG",
        "HEIC",
        "RAW",
        "PSD",
        "JXL",
      ],
      color: "from-blue-500 to-cyan-500",
      description:
        "Professional image conversion with advanced upscaling, color enhancement, format optimization for web and print. Support for next-gen formats like AVIF, JXL, and HEIC with intelligent compression.",
      seoKeywords:
        "image converter, HEIC to JPEG, PNG to WebP, AVIF converter, advanced image optimization",
    },
    {
      category: "High-Fidelity Audio Processing",
      icon: FileAudio,
      count: 68,
      link: "/audio-converter",
      formats: [
        "MP3",
        "FLAC",
        "WAV",
        "AAC",
        "OGG",
        "M4A",
        "OPUS",
        "AMR",
        "DTS",
        "AC3",
      ],
      color: "from-purple-500 to-violet-500",
      description:
        "Professional audio conversion with advanced noise reduction, metadata preservation, and spatial audio support. Lossless FLAC, hi-res formats, and pristine quality preservation.",
      seoKeywords:
        "professional audio converter, FLAC to MP3, spatial audio, advanced noise reduction, lossless audio",
    },
    {
      category: "4K/8K Video Processing",
      icon: FileVideo,
      count: 39,
      link: "/video-converter",
      formats: [
        "MP4",
        "AVI",
        "MOV",
        "MKV",
        "WebM",
        "HEVC",
        "AV1",
        "VP9",
        "AVCHD",
        "ProRes",
      ],
      color: "from-red-500 to-pink-500",
      description:
        "Advanced video conversion with 8K support, professional upscaling, HDR processing, and next-gen codecs like AV1. High-efficiency encoding with VP9, HEVC, and ProRes support.",
      seoKeywords:
        "4K video converter, 8K processing, AV1 codec, HDR video, professional video upscaling",
    },
    {
      category: "Advanced Document Processing",
      icon: FileText,
      count: 36,
      link: "/document-converter",
      formats: [
        "PDF",
        "DOCX",
        "XLSX",
        "PPTX",
        "ODT",
        "RTF",
        "PAGES",
        "EPUB",
        "MOBI",
        "AZW",
      ],
      color: "from-green-500 to-emerald-500",
      description:
        "Professional document conversion with OCR, layout preservation, and advanced text extraction. Seamless PDF, Word, Excel, and PowerPoint processing with formatting intact.",
      seoKeywords:
        "PDF converter, OCR document processing, DOCX to PDF, advanced text extraction",
    },
    {
      category: "Advanced Archive Processing",
      icon: Archive,
      count: 30,
      link: "/archive-converter",
      formats: [
        "ZIP",
        "RAR",
        "7Z",
        "TAR",
        "GZIP",
        "BZ2",
        "XZ",
        "LZO",
        "ZSTD",
        "BROTLI",
      ],
      color: "from-orange-500 to-yellow-500",
      description:
        "Modern compression with next-gen algorithms like Zstandard and Brotli for optimal file sizes. Maximum compression ratios with ultra-fast decompression speeds.",
      seoKeywords:
        "modern compression, ZSTD converter, Brotli compression, high-ratio archiving",
    },
    {
      category: "Web Font Optimization",
      icon: StickyNote,
      count: 17,
      link: "/font-converter",
      formats: [
        "TTF",
        "OTF",
        "WOFF",
        "WOFF2",
        "EOT",
        "PFB",
        "UFO",
        "CID",
        "WOFF3",
        "SVG",
      ],
      color: "from-indigo-500 to-purple-500",
      description:
        "Next-gen web font conversion with subset optimization and modern format support. WOFF2 optimization for faster page loads and improved Core Web Vitals.",
      seoKeywords:
        "web font optimizer, WOFF2 converter, font subsetting, performance optimization",
    },
    {
      category: "CAD & 3D Model Processing",
      icon: Boxes,
      count: 16,
      link: "/3d-converter",
      formats: [
        "STL",
        "OBJ",
        "FBX",
        "GLTF",
        "3DS",
        "PLY",
        "DAE",
        "USD",
        "STEP",
        "IGES",
      ],
      color: "from-teal-500 to-green-500",
      description:
        "Professional CAD and 3D model conversion with precision geometry and material preservation. Industry-standard formats for 3D printing, AR/VR, and engineering workflows.",
      seoKeywords:
        "CAD converter, 3D printing STL, STEP file converter, industrial 3D processing",
    },
    {
      category: "Enterprise Presentations",
      icon: Presentation,
      count: 12,
      link: "/presentation-converter",
      formats: [
        "PPTX",
        "PPT",
        "ODP",
        "KEY",
        "PPS",
        "PPSX",
        "POTX",
        "FODP",
        "SXI",
        "UOP",
      ],
      color: "from-pink-500 to-rose-500",
      description:
        "Business presentation conversion with animation preservation and brand consistency. Convert PowerPoint, Keynote, and Google Slides while maintaining transitions and formatting.",
      seoKeywords:
        "enterprise presentation converter, PowerPoint automation, brand-safe conversion",
    },
  ];

  const features = [
    {
      icon: Zap,
      title: "Lightning Fast Conversion",
      description:
        "Convert files in seconds with our optimized distributed processing infrastructure, GPU-accelerated engines, and intelligent format detection for optimal quality",
      highlight: "High-performance, 10x faster conversion",
      seoKeywords:
        "fast file converter, fast conversion, intelligent file processing, GPU acceleration",
    },
    {
      icon: Shield,
      title: "Zero-Trust Security Architecture",
      description:
        "All files processed securely in the cloud using AWS S3 infrastructure with military-grade end-to-end encryption, zero-knowledge architecture, automatic file deletion, SOC 2 Type II, ISO 27001, and GDPR compliance",
      highlight: "SOC 2, ISO 27001, GDPR compliant • Cloud S3 Processing",
      seoKeywords:
        "secure file converter, zero-trust security, encrypted conversion, compliance, cloud S3 processing",
    },
    {
      icon: Crown,
      title: "Enhanced Quality Preservation",
      description:
        "Advanced algorithms maintain and enhance original quality with intelligent color profile management, metadata preservation, and format-specific optimizations",
      highlight: "Professional lossless quality",
      seoKeywords:
        "quality enhancement, lossless conversion, professional converter, quality preservation",
    },
    {
      icon: Globe,
      title: "Cloud Storage Integration",
      description:
        "All conversions processed in the cloud using AWS S3. Seamlessly integrate with Google Drive, Dropbox, OneDrive, Box, and 15+ cloud storage providers for automated workflow processing",
      highlight: "15+ cloud storage integrations • S3-powered processing",
      seoKeywords:
        "cloud storage converter, automated workflows, S3 integration, cloud processing",
    },
    {
      icon: Database,
      title: "Enterprise API & Webhooks",
      description:
        "RESTful API v2 with real-time webhooks, GraphQL support, comprehensive SDKs, rate limiting, usage analytics, and enterprise-grade monitoring",
      highlight: "API v2 with webhooks & GraphQL",
      seoKeywords:
        "file conversion API, webhooks, GraphQL API, enterprise integration, SDK",
    },
    {
      icon: TrendingUp,
      title: "Auto-Scaling Infrastructure",
      description:
        "Serverless architecture that auto-scales from single files to enterprise workloads with dedicated processing clusters and guaranteed SLAs",
      highlight: "Serverless auto-scaling architecture",
      seoKeywords:
        "serverless converter, auto-scaling, enterprise SLA, dedicated infrastructure",
    },
    {
      icon: ScanText,
      title: "Multilingual OCR — Text Extraction",
      description:
        "Extract editable, searchable text from scanned PDFs, photos, and image-based documents in 77 languages. Convert scanned contracts, invoices, and forms to Word, Excel, TXT, or searchable PDF with high accuracy.",
      highlight: "77 languages • scanned PDF & image support",
      seoKeywords:
        "OCR converter, scanned PDF to text, image to Word, multilingual OCR, text extraction",
    },
  ];

  // Dynamic popular conversions are loaded via useEffect above

  const stats = [
    {
      value: `${dynamicStats.formatsCount}+`,
      label: "Supported File Formats",
      icon: FileText,
      description: "Including latest formats & codecs",
    },
    {
      value: `${(dynamicStats.conversionPairsCount / 1000).toFixed(0)}K+`,
      label: "Conversion Pairs",
      icon: ArrowRight,
      description: "Pre-configured conversions ready",
    },
    {
      value: dynamicStats.categoriesCount.toString(),
      label: "Format Categories",
      icon: Layers3,
      description: "Professional conversion engines",
    },
    {
      value: `10M+`,
      label: "Files Converted",
      icon: TrendingUp,
      description: "Processing 2M+ files monthly",
    },
    {
      value: `99.99%`,
      label: "Success Rate",
      icon: Shield,
      description: "Enterprise-grade reliability with S3 cloud processing",
    },
  ];

  const conversionKeywords = [
    "PDF converter",
    "image converter",
    "video converter",
    "audio converter",
    "HEIC to JPEG",
    "PDF to DOCX",
    "MP4 converter",
    "3D model converter",
    "STL converter",
    "CAD converter",
    "batch converter",
    "online file converter",
    "free converter",
    "professional converter",
    "lossless conversion",
    "secure converter",
    "API file converter",
    "enterprise converter",
  ];

  return (
    <div className="bg-white">
      <SEOMeta
        title="Docpose.cloud — Convert Files Online (432+ Formats) & API"
        description="Convert documents, images, audio, video, CAD, and 3D files online. Docpose.cloud supports 432+ formats and 33K+ conversion pairs, provides a secure REST API for automated file conversion."
        structuredData={structuredData}
        canonical="https://docpose.cloud/"
        robots="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        breadcrumbs={[{ name: "Home", url: "/" }]}
        ogType="website"
        ogImage="/docpose-cloud-og.png"
      />

      {/* Hero Section - the file uploader IS the hero, so the tool is the
          first thing every visitor (mobile or desktop) sees and can use
          without leaving the page or creating an account. */}
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8">
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Convert Any File, Instantly — Free
            </h1>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Drop a file below to convert it online. No account, no
              software, no waiting.
            </p>
          </div>

          {/* Universal converter, embedded directly in the hero. Reserved
              min-height on the Suspense fallback matches the collapsed
              uploader height to avoid a layout shift while the chunk loads. */}
          <Suspense
            fallback={
              <div
                className="h-[620px] sm:h-[580px] rounded-2xl border-2 border-dashed border-gray-300 bg-white/60 animate-pulse"
                aria-hidden="true"
              />
            }
          >
            <ConvertPage embedded routeAnalysis={HOME_ROUTE_ANALYSIS} />
          </Suspense>

          {/* Value sub-header below the uploader */}
          <p className="text-center text-xs sm:text-sm text-gray-500 mt-6 max-w-3xl mx-auto">
            Supporting <strong>{dynamicStats.formatsCount}+ Formats</strong>{" "}
            &amp;{" "}
            <strong>
              {(dynamicStats.conversionPairsCount / 1000).toFixed(0)}k+ Pairs
            </strong>
            . Up to <strong>100 concurrent conversions</strong> with Pro. 10x
            faster than the competition.
          </p>

          {/* Aggregate Rating */}
          <div className="mt-4 flex justify-center">
            <AggregateRating />
          </div>
        </div>
      </section>

      {/* Pricing Quick Comparison - directly below the hero */}
      <QuickComparisonCard />

      {/* Search Panel Section */}
      <section className="py-4 bg-white border-b border-gray-200">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={heroSearchRef}>
            <form onSubmit={handleHeroSearch} className="relative">
              <div className="relative group">
                <Search className="absolute left-5 sm:left-6 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-primary transition-colors w-6 h-6 sm:w-7 sm:h-7 z-10" />
                <Input
                  type="text"
                  placeholder="Search formats (PDF, JPEG, MP4), conversions (PDF to DOCX), categories..."
                  value={heroSearchTerm}
                  onChange={(e) => setHeroSearchTerm(e.target.value)}
                  className="w-full h-16 sm:h-20 pl-14 sm:pl-16 pr-32 sm:pr-40 text-base sm:text-lg bg-white border-2 border-gray-200 hover:border-primary/50 focus:border-primary shadow-lg hover:shadow-xl focus:shadow-2xl transition-all duration-200 rounded-2xl placeholder:text-gray-400"
                />
                {heroSearchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setHeroSearchTerm("");
                      setHeroSearchResults([]);
                      setShowHeroResults(false);
                    }}
                    className="absolute right-24 sm:right-32 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors z-10"
                  >
                    <X className="w-6 h-6" />
                  </button>
                )}
                <Button
                  type="submit"
                  size="lg"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-12 sm:h-16 px-6 sm:px-8 bg-gradient-to-r from-primary to-brand-600 hover:from-primary/90 hover:to-brand-600/90 shadow-md hover:shadow-lg transition-all z-10"
                >
                  <span className="hidden sm:inline">Search</span>
                  <Search className="w-5 h-5 sm:hidden" />
                </Button>
              </div>

              {/* Search Results Dropdown */}
              {showHeroResults && heroSearchTerm.length >= 2 && (
                <div className="absolute top-full mt-2 w-full z-[100]">
                  {/* Close Button - Centered Above Panel */}
                  <div className="flex justify-center mb-2">
                    <button
                      type="button"
                      onClick={() => setShowHeroResults(false)}
                      className="bg-white rounded-full p-2 shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
                      aria-label="Close search results"
                    >
                      <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
                    </button>
                  </div>

                  {/* Results Panel */}
                  <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      {heroSearchLoading && (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-primary" />
                          <span className="ml-2 text-base text-gray-700 font-medium">
                            Searching...
                          </span>
                        </div>
                      )}

                      {!heroSearchLoading && heroSearchResults.length === 0 && (
                        <div className="p-8 text-center">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-base text-gray-700 font-medium">
                            No results found for "{heroSearchTerm}"
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            Try searching for a format, conversion, or category
                          </p>
                        </div>
                      )}

                      {!heroSearchLoading && heroSearchResults.length > 0 && (
                        <div className="divide-y divide-gray-100">
                          {heroSearchResults.map(
                            (result: any, index: number) => (
                              <Link
                                key={index}
                                to={result.url}
                                onClick={handleHeroResultClick}
                                className={cn(
                                  "block p-4 hover:bg-gray-50 transition-colors group",
                                  result.type === "view-all" &&
                                    "bg-orange-50/50 hover:bg-orange-100/50 border-t-2 border-orange-200",
                                )}
                              >
                                <div className="flex items-start gap-3">
                                  <span className="text-2xl mt-0.5">
                                    {getResultIcon(result.type)}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-base text-gray-900 group-hover:text-primary transition-colors">
                                        {result.title}
                                      </h4>
                                      {result.badge && (
                                        <Badge
                                          variant="secondary"
                                          className={cn(
                                            "text-xs font-medium",
                                            getResultBadgeColor(result.type),
                                          )}
                                        >
                                          {result.badge}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600 line-clamp-1">
                                      {result.description}
                                    </p>
                                  </div>
                                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                                </div>
                              </Link>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <p className="mt-3 text-center text-sm text-gray-500">
                <span className="hidden sm:inline">
                  Try: "PDF to DOCX", "HEIC to JPEG", "video converter", or
                  browse {dynamicStats.formatsCount}
                  <span className="ml-0.5">
                    +{" "}
                    <Link
                      to="/formats"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      supported formats
                    </Link>
                  </span>
                </span>
                <span className="sm:hidden">
                  Try: "PDF", "video converter", or browse{" "}
                  {dynamicStats.formatsCount}+ formats
                </span>
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* What is Docpose.cloud? - Explanatory Content Block */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            What is Docpose.cloud?
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Docpose.cloud is an online file conversion platform that lets you
            convert documents, images, audio, video, CAD, and 3D files directly
            in your browser — and extract text from scanned files using built-in OCR. All files are processed securely in the cloud using AWS S3 infrastructure with automatic encryption and deletion. It also provides a{" "}
            <Link
              to="/api-docs"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              REST API
            </Link>{" "}
            for developers and businesses that need automated, large-scale file
            conversion with predictable quality and security. Supporting {dynamicStats.formatsCount}+ formats and {(dynamicStats.conversionPairsCount / 1000).toFixed(0)}K+ conversion pairs, browse our{" "}
            <Link
              to="/formats"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              complete format directory
            </Link>{" "}
            or check{" "}
            <Link
              to="/pricing"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              pricing and limits
            </Link>{" "}
            for enterprise plans.
          </p>
        </div>
      </section>

      {/* Format Categories */}
      <section
        className="py-20 bg-gray-50"
        itemScope
        itemType="https://schema.org/ItemList"
        style={{ contentVisibility: "auto", containIntrinsicSize: "1100px" }}
      >
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 px-4"
              itemProp="name"
            >
              <span className="hidden sm:inline">
                Professional Format Support: {dynamicStats.formatsCount}+
                Formats Across {dynamicStats.categoriesCount} Categories
              </span>
              <span className="sm:hidden">
                {dynamicStats.formatsCount}+ Formats in{" "}
                {dynamicStats.categoriesCount} Categories
              </span>
            </h2>
            <p className="text-sm sm:text-base text-gray-600 w-full mx-auto px-4">
              From advanced image optimization and next-gen video codecs to
              precision <strong>CAD & 3D model processing</strong>,{" "}
              <strong>spatial audio conversion</strong>, modern web formats like
              <strong> AVIF & JXL</strong>, and{" "}
              <strong>enterprise automation workflows</strong> - our
              professional engines support every format you need with
              intelligent optimization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportedCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Link
                  key={index}
                  to={category.link}
                  aria-label={`Go to ${category.category}`}
                >
                  <Card
                    className="group hover:shadow-2xl transition-all duration-300 h-full shadow-sm cursor-pointer"
                    itemScope
                    itemType="https://schema.org/Thing"
                    itemProp="itemListElement"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className={`w-14 h-14 rounded-lg bg-gradient-to-r ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform`}
                        >
                          <IconComponent
                            className="w-7 h-7 text-white"
                            aria-label={`${category.category} icon`}
                          />
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {category.count} formats
                        </Badge>
                      </div>

                      <h3
                        className="text-sm sm:text-base font-semibold text-gray-900 mb-2"
                        itemProp="name"
                      >
                        {category.category}
                      </h3>
                      <p
                        className="text-xs sm:text-sm text-gray-600 mb-4"
                        itemProp="description"
                      >
                        {category.description}
                      </p>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {category.formats
                          .slice(0, 6)
                          .map((format, formatIndex) => (
                            <span
                              key={formatIndex}
                              className="text-xs sm:text-sm px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded-md"
                            >
                              {format}
                            </span>
                          ))}
                        {category.count > 6 && (
                          <span className="text-xs sm:text-sm px-2 sm:px-3 py-1 bg-gray-100 text-gray-500 rounded-md">
                            +{category.count - 6} more
                          </span>
                        )}
                      </div>

                      <div
                        className="text-xs text-gray-500 mt-2"
                        aria-label={`SEO keywords: ${category.seoKeywords}`}
                      >
                        Popular:{" "}
                        {category.seoKeywords
                          .split(", ")
                          .slice(0, 3)
                          .join(", ")}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12 px-4">
            <Link to="/formats" className="inline-block w-full sm:w-auto">
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-sm sm:text-base px-4 sm:px-6"
              >
                <span className="hidden sm:inline">
                  View Complete List of {dynamicStats.formatsCount}+ Supported
                  Formats
                </span>
                <span className="sm:hidden">
                  View All {dynamicStats.formatsCount}+ Formats
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Popular Conversions */}
      <section
        className="py-20"
        style={{ contentVisibility: "auto", containIntrinsicSize: "1200px" }}
      >
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 px-4">
              Most Popular File Conversion Tools
            </h2>
            <p className="text-sm sm:text-base text-gray-600 w-full mx-auto px-4">
              Conversion tools ranked by{" "}
              <strong>real user reviews and popularity</strong>. See verified
              ratings from professionals who have successfully converted their
              files. All conversions maintain original quality with advanced
              optimization algorithms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {isLoading
              ? // Loading skeleton
                Array.from({ length: 6 }).map((_, index) => (
                  <Card key={index} className="h-full animate-pulse">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                        <div className="w-16 h-5 bg-gray-200 rounded"></div>
                      </div>
                      <div className="w-3/4 h-5 bg-gray-200 rounded mb-2"></div>
                      <div className="w-full h-16 bg-gray-200 rounded mb-4"></div>
                      <div className="w-1/2 h-4 bg-gray-200 rounded"></div>
                    </CardContent>
                  </Card>
                ))
              : popularConversions.map((tool, index) => {
                  const IconComponent = getIconComponent(tool.icon);
                  let slug = "convert";
                  try {
                    slug = buildFormatPairConverterSlug(tool.from, tool.to);
                  } catch (error) {
                    console.warn("Skipping malformed popular conversion link:", tool, error);
                  }
                  return (
                    <Link
                      key={index}
                      to={`/${slug}`}
                      aria-label={tool.seoTitle}
                    >
                      <Card className="group hover:shadow-2xl transition-all duration-300 h-full shadow-sm">
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div
                              className={`w-14 h-14 rounded-lg bg-gradient-to-r ${tool.color} flex items-center justify-center group-hover:scale-110 transition-transform`}
                            >
                              <IconComponent
                                className="w-7 h-7 text-white"
                                aria-label={`${tool.category} conversion icon`}
                              />
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="outline" className="text-xs">
                                {tool.category}
                              </Badge>

                              {/* Rating Display - Real User Reviews */}
                              {tool.votingCount && tool.votingCount > 0 ? (
                                <div className="flex items-center gap-1">
                                  <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        className={`w-3 h-3 ${
                                          i < Math.round(tool.ratingValue || 0)
                                            ? "text-yellow-400 fill-current"
                                            : "text-gray-300"
                                        }`}
                                      />
                                    ))}
                                  </div>
                                  <span className="text-xs font-semibold text-gray-900">
                                    {tool.ratingValue?.toFixed(1)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    ({tool.votingCount}{" "}
                                    {tool.votingCount === 1
                                      ? "review"
                                      : "reviews"}
                                    )
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        className="w-3 h-3 text-gray-300"
                                      />
                                    ))}
                                  </div>
                                  <span className="text-xs text-gray-400 italic">
                                    No reviews yet
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">
                            {tool.from} to {tool.to} Converter
                          </h3>
                          <p className="text-gray-600 text-xs sm:text-sm mb-4">
                            {tool.description}
                          </p>
                          <div className="text-primary text-xs sm:text-sm font-medium group-hover:text-primary/80 flex items-center focus:outline-none focus:ring-2 focus:ring-primary/40">
                            <span className="hidden sm:inline">
                              Convert {tool.from} to {tool.to} Now
                            </span>
                            <span className="sm:hidden">Convert Now</span>{" "}
                            <ArrowRight className="ml-1 w-3 h-3 sm:w-4 sm:h-4" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
          </div>

          <div className="text-center px-4">
            <Link to="/convert" className="inline-block w-full sm:w-auto">
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-sm sm:text-base px-4 sm:px-6"
              >
                <MousePointer className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">
                  Open Universal File Converter - All{" "}
                  {dynamicStats.formatsCount}+ Formats
                </span>
                <span className="sm:hidden">Open Converter</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* REST API quickstart - moved below the web tool sections per
          "no-account-required web tool first" ordering: visitors evaluating
          the product should see it working in the browser before being
          pitched the API. */}
      <div style={{ contentVisibility: "auto", containIntrinsicSize: "700px" }}>
        <Suspense
          fallback={
            <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
              <div className="h-[700px] rounded-3xl border border-gray-200 bg-gray-50" />
            </div>
          }
        >
          <DevQuickStart />
        </Suspense>
      </div>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 px-4">
              Why Industry Leaders Choose Docpose.Cloud's Platform?
            </h2>
            <p className="text-sm sm:text-base text-gray-600 w-full mx-auto px-4">
              Next-generation conversion platform with zero-trust security,
              auto-scaling serverless architecture, and enterprise APIs designed
              for mission-critical workflows that demand reliability, security,
              and intelligent optimization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="group text-center px-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-primary to-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent
                      className="w-7 h-7 sm:w-8 sm:h-8 text-white"
                      aria-label={`${feature.title} feature icon`}
                    />
                  </div>
                  <div className="mb-3">
                    <Badge variant="secondary" className="mb-2 text-xs">
                      {feature.highlight}
                    </Badge>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600">
                    {feature.description}
                  </p>
                  <div
                    className="text-xs text-gray-400 mt-2"
                    aria-label={`Related keywords: ${feature.seoKeywords}`}
                  >
                    {feature.seoKeywords}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials - Lazy loaded */}
      {/* Real Social Proof: as an early-stage product without a large
          customer base to quote, verifiable security facts build more
          trust than generic "Industry Leader" testimonials would. */}
      <LazySection>
        <SecurityTrustBar />
      </LazySection>

      {/* FAQ Section for SEO — uses content-visibility instead of a
          mount/unmount LazySection placeholder swap, since replacing a fixed
          placeholder height with this section's real (much taller) height
          was itself a major CLS source. content-visibility:auto keeps the
          section's box reserved at contain-intrinsic-size from first paint
          and defers render cost until it nears the viewport, with zero
          layout jump and no change to the SSR-visible text content. */}
      <section className="py-20 bg-gray-50 [content-visibility:auto] [contain-intrinsic-size:auto_2200px] md:[contain-intrinsic-size:auto_1600px]">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 px-4">
                Frequently Asked Questions About File Conversion
              </h2>
              <p className="text-sm sm:text-base text-gray-600 px-4">
                Common questions about our professional file conversion service
                and supported formats.
              </p>
            </div>

            <div className="space-y-6 sm:space-y-8">
              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  What file formats does Docpose.Cloud support?
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Docpose.Cloud supports 432+ file formats across 18
                  categories including:
                  <strong> Images</strong> (JPEG, PNG, WebP, HEIC, TIFF, RAW,
                  PSD),
                  <strong> Videos</strong> (MP4, AVI, MOV, MKV, WebM, HEVC),
                  <strong> Documents</strong> (PDF, DOCX, XLSX, PPTX),
                  <strong> Audio</strong> (MP3, FLAC, WAV, AAC),
                  <strong> 3D Models</strong> (STL, OBJ, FBX, GLTF),
                  <strong> CAD Files</strong> (DWG, DXF, DWF),
                  <strong> Archives</strong> (ZIP, RAR, 7Z), and many more
                  specialized formats.
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  Is Docpose.Cloud free to use for file conversion?
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Yes, Docpose.Cloud offers a free plan with basic conversion
                  features for individual users. Professional plans with API
                  access, batch processing up to 100 files, priority processing,
                  and advanced features are available for businesses,
                  developers, and power users who need enterprise-grade
                  conversion capabilities.
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  How secure is file conversion on Docpose.Cloud?
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Docpose.Cloud uses enterprise-grade security with end-to-end
                  encryption, automatic file deletion after 24 hours, SOC 2
                  compliance, and GDPR compliance for data protection. All
                  conversions are processed on secure servers with bank-grade
                  security protocols. Your files are never stored permanently
                  and are automatically deleted after processing.
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  Does Docpose.Cloud maintain file quality during conversion?
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Yes, Docpose.Cloud uses advanced conversion algorithms
                  designed to maintain original quality. We support lossless
                  conversion for formats that allow it, preserve color profiles
                  for images, maintain metadata for audio files, and use
                  format-specific optimizations to ensure the highest possible
                  output quality for your converted files.
                </p>
              </div>

              <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  Does Docpose.Cloud support OCR — extracting text from scanned documents?
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Yes. Docpose.Cloud includes built-in OCR (Optical Character Recognition) that can extract
                  editable, searchable text from scanned PDFs, image files (JPG, PNG, TIFF), and
                  image-based documents. OCR supports 77 languages including English, French, German,
                  Spanish, Arabic, Chinese, Japanese, and more. You can convert scanned files to editable
                  Word, Excel, TXT, or searchable PDF. OCR conversions use 3–5 conversion credits per page
                  due to the additional compute required. Visit the{" "}
                  <Link
                    to="/ocr"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    OCR converter
                  </Link>{" "}
                  to get started.
                </p>
              </div>
            </div>
          </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-primary to-brand-600">
        <div className="w-full max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4">
            Ready for Next-Generation File Conversion?
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-white/90 max-w-3xl mx-auto mb-8">
            Join 100,000+ professionals processing 2M+ files monthly with our
            professional platform. Experience zero-trust security, auto-scaling
            infrastructure, and intelligent optimization with enterprise APIs,
            real-time webhooks, and 99.99% uptime SLA.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/convert"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-full sm:w-auto bg-white text-black hover:bg-gray-50 px-4 sm:px-6 py-3 sm:text-base shadow-md"
            >
              <span className="hidden sm:inline">Start Converting Free</span>
              <span className="sm:hidden">Start Converting Free</span>{" "}
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-full sm:w-auto border-2 border-white bg-white/10 text-white hover:bg-white/20 px-4 sm:px-6 py-3 sm:text-base"
            >
              <span className="hidden sm:inline">
                Enterprise API v2 • Webhooks • Auto-Scaling
              </span>
              <span className="sm:hidden">View Plans</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
